import { useEffect, useState, type FormEvent } from 'react';
import { Page } from '../components/Page.js';

type Reminder = {
  id: number;
  dedupe_key: string;
  rule_id: string;
  rule_title: string;
  basis: string;
  plain: string;
  subject_type: string;
  subject_id: string | null;
  subject_label: string | null;
  trigger_date: string;
  due_date: string;
  state: 'overdue' | 'due_soon' | 'upcoming' | 'snoozed' | 'satisfied';
  days_to_due: number;
  satisfied_date: string | null;
  snoozed_until: string | null;
};
type Counts = { actionable: number; overdue: number; due_soon: number; total: number };

const STATE_BADGE: Record<Reminder['state'], { kind: string; label: (r: Reminder) => string }> = {
  overdue: { kind: 'bad', label: (r) => `Overdue ${Math.abs(r.days_to_due)}d` },
  due_soon: { kind: 'warn', label: (r) => (r.days_to_due === 0 ? 'Due today' : `Due ${r.days_to_due}d`) },
  upcoming: { kind: 'mute', label: (r) => `In ${r.days_to_due}d` },
  snoozed: { kind: 'mute', label: (r) => `Snoozed → ${r.snoozed_until ?? ''}` },
  satisfied: { kind: 'ok', label: (r) => `Notified ${r.satisfied_date ?? ''}` },
};
const STATE_DOT: Record<Reminder['state'], string> = {
  overdue: 'bad', due_soon: 'warn', upcoming: 'ok', snoozed: 'warn', satisfied: 'ok',
};
const todayISO = () => new Date().toISOString().slice(0, 10);

export function Deadlines() {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [showSatisfied, setShowSatisfied] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  // Eligible for a full in-person re-notification: open statutory items, EXCEPT
  // foreign travel (event-specific; handled on its own).
  const fullEligible = reminders.filter((r) => r.state !== 'satisfied' && r.rule_id !== 'R11' && r.rule_id !== 'R11b');

  async function load() {
    try {
      const r = await fetch('/api/reminders').then((x) => x.json());
      setReminders(r.reminders ?? []);
      setCounts(r.counts ?? null);
    } catch { /* offline */ }
  }
  useEffect(() => { load(); }, []);

  const visible = reminders.filter((r) => showSatisfied || r.state !== 'satisfied');

  return (
    <Page
      eyebrow="Compliance"
      title="What I need to do"
      actions={
        <span className={`pill ${counts && counts.actionable > 0 ? 'pill-ok' : 'pill-ok'}`}>
          <span className={`dot dot-${counts && counts.overdue > 0 ? 'bad' : counts && counts.due_soon > 0 ? 'warn' : 'ok'}`} />
          {counts ? `${counts.overdue} overdue · ${counts.due_soon} due soon` : 'Loading…'}
        </span>
      }
    >
      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          These deadlines are computed from your records — they are an <b>aid, not the legal record</b>.
          The only way to clear a statutory item is to <b>record the real notification</b> you made to the
          police, which is itself written to the tamper-evident ledger and anchored. You can snooze a
          reminder to nudge it, but it cannot be deleted.
        </p>
      </section>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 10, flexWrap: 'wrap' }}>
        {fullEligible.length > 0
          ? <button className="btn btn-primary" type="button" onClick={() => setFullOpen((o) => !o)}>{fullOpen ? 'Close' : '✓ I did a full notification in person'}</button>
          : <span />}
        <label className="muted" style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={showSatisfied} onChange={(e) => setShowSatisfied(e.target.checked)} />
          show completed
        </label>
      </div>
      {fullOpen && <FullRenotifyForm eligible={fullEligible} onDone={() => { setFullOpen(false); load(); }} />}

      <div style={{ display: 'grid', gap: 12 }}>
        {visible.length === 0 && (
          <div className="card card-pad muted" style={{ fontSize: 13 }}>
            Nothing outstanding. New deadlines appear here automatically as you add devices, accounts and travel.
          </div>
        )}
        {visible.map((r) => {
          const badge = STATE_BADGE[r.state];
          const done = r.state === 'satisfied';
          return (
            <div className="card card-pad" key={r.id} style={{ opacity: done ? 0.7 : 1 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span className={`dot dot-${STATE_DOT[r.state]}`} style={{ marginTop: 5 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 14.5 }}>{r.rule_title}</strong>
                    <span className={`badge badge-${badge.kind}`}>{badge.label(r)}</span>
                    <span className="badge badge-mute">{r.rule_id}</span>
                  </div>
                  {r.subject_label && <div style={{ fontSize: 13.5, marginTop: 2 }}>{r.subject_label}</div>}
                  <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>{r.plain}</div>
                  <div className="muted3" style={{ fontSize: 12, marginTop: 4 }}>
                    {done
                      ? `Discharged by a recorded notification on ${r.satisfied_date}.`
                      : `Triggered ${r.trigger_date} · deadline ${r.due_date}`}
                    {' · '}<span title={r.basis} style={{ borderBottom: '1px dotted var(--ink3)', cursor: 'help' }}>basis</span>
                  </div>

                  {!done && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button className="btn btn-primary" type="button"
                        onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                        Record that I told the police
                      </button>
                      {r.state !== 'snoozed' && (
                        <button className="btn btn-ghost" type="button"
                          onClick={() => snooze(r.id, load)}>Snooze 7 days</button>
                      )}
                    </div>
                  )}

                  {openId === r.id && (
                    <RecordForm reminder={r} onDone={() => { setOpenId(null); load(); }} />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Page>
  );
}

async function snooze(id: number, reload: () => void) {
  const until = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
  await fetch(`/api/reminders/${id}/snooze`, {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ until, note: 'snoozed from deadlines' }),
  });
  reload();
}

function RecordForm({ reminder, onDone }: { reminder: Reminder; onDone: () => void }) {
  const [date, setDate] = useState(todayISO());
  const [method, setMethod] = useState('in_person');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // After a successful notification POST, store the uuid and show the proof step
  const [notifUuid, setNotifUuid] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const res = await fetch('/api/notifications', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          rule_id: reminder.rule_id,
          subject_type: reminder.subject_type,
          subject_id: reminder.subject_id,
          subject_label: reminder.subject_label,
          what: `${reminder.rule_title}${reminder.subject_label ? ' — ' + reminder.subject_label : ''}`,
          notified_date: date,
          method,
          reference,
          satisfies_key: reminder.dedupe_key,
        }),
      });
      if (!res.ok) { setErr((await res.json()).error ?? 'Failed to record'); return; }
      const { uuid } = await res.json();
      setNotifUuid(uuid ?? null);
      if (!uuid) onDone(); // fallback if uuid absent
    } catch { setErr('Could not reach the server'); }
    finally { setBusy(false); }
  }

  // Proof upload step shown after notification is saved
  if (notifUuid) {
    return <ProofStep notifUuid={notifUuid} onDone={onDone} />;
  }

  return (
    <form onSubmit={submit} style={{ marginTop: 12, padding: 12, background: 'var(--surface2, rgba(0,0,0,0.03))', borderRadius: 8 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label className="fld" style={{ flex: '1 1 200px' }}><span>Date you attended the police station (in person)</span>
          <input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} />
          <small className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>
            Emailing or phoning isn't notification — only attending in person counts (SOA 2003 s.87). Use the date you attended and got the written acknowledgement.
          </small>
        </label>
        <label className="fld" style={{ flex: '1 1 120px' }}><span>How</span>
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="in_person">In person (notification)</option>
            <option value="online">Online form (does not discharge)</option>
            <option value="phone">Phone (does not discharge)</option>
            <option value="letter">Letter (does not discharge)</option>
            <option value="email">Email (does not discharge)</option>
          </select>
        </label>
        <label className="fld" style={{ flex: '1 1 160px' }}><span>Reference / officer (optional)</span>
          <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. PPU ref / officer name" />
        </label>
      </div>
      {err && <div style={{ color: 'var(--bad)', fontSize: 12.5, margin: '4px 0' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Recording…' : 'Save notification (anchors to ledger)'}
        </button>
        <button className="btn btn-ghost" type="button" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function ProofStep({ notifUuid, onDone }: { notifUuid: string; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function upload() {
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('entity_type', 'notification');
      fd.append('entity_uuid', notifUuid);
      fd.append('kind', 'proof');
      fd.append('file', file);
      const res = await fetch('/api/attachments', { method: 'POST', body: fd });
      if (!res.ok) { setErr('Upload failed — you can attach this later from Notifications.'); return; }
      setUploaded(true);
    } catch { setErr('Could not reach the server — you can attach this later from Notifications.'); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 12, padding: 12, background: 'var(--surface2, rgba(0,0,0,0.03))', borderRadius: 8 }}>
      <p style={{ margin: '0 0 8px', fontSize: 13.5, fontWeight: 600 }}>Notification saved.</p>
      {!uploaded ? (
        <>
          <p className="muted" style={{ margin: '0 0 10px', fontSize: 13 }}>
            Attach the written acknowledgement the police gave you (photo or scan). This is evidence only — the notification is already recorded.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            <button className="btn btn-primary" type="button" disabled={!file || busy} onClick={upload}>
              {busy ? 'Uploading…' : 'Upload acknowledgement'}
            </button>
            <button className="btn btn-ghost" type="button" onClick={onDone}>Skip</button>
          </div>
          {err && <div style={{ color: 'var(--bad)', fontSize: 12.5, marginTop: 6 }}>{err}</div>}
        </>
      ) : (
        <>
          <p className="muted" style={{ margin: '0 0 10px', fontSize: 13 }}>Acknowledgement uploaded. You can view it under Notifications.</p>
          <button className="btn btn-primary" type="button" onClick={onDone}>Done</button>
        </>
      )}
    </div>
  );
}

function FullRenotifyForm({ eligible, onDone }: { eligible: Reminder[]; onDone: () => void }) {
  const [date, setDate] = useState(todayISO());
  const [checked, setChecked] = useState<Record<number, boolean>>(() => Object.fromEntries(eligible.map((r) => [r.id, true])));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notifUuid, setNotifUuid] = useState<string | null>(null);

  if (notifUuid) {
    return <div className="card card-pad" style={{ marginBottom: 16 }}><ProofStep notifUuid={notifUuid} onDone={onDone} /></div>;
  }

  const selected = eligible.filter((r) => checked[r.id]);
  async function submit(e: FormEvent) {
    e.preventDefault(); setErr(null);
    if (selected.length === 0) { setErr('Tick at least one item you re-stated.'); return; }
    setBusy(true);
    try {
      const items = selected.map((r) => ({ dedupe_key: r.dedupe_key, rule_id: r.rule_id, subject_type: r.subject_type, subject_id: r.subject_id ?? undefined, subject_label: r.subject_label ?? undefined, what: r.rule_title + (r.subject_label ? ` — ${r.subject_label}` : '') }));
      const res = await fetch('/api/notifications/full', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ notified_date: date, method: 'in_person', reference: reference || undefined, items }),
      });
      if (!res.ok) { setErr((await res.json()).error ?? 'Failed'); return; }
      const j = await res.json();
      if (j.uuid) setNotifUuid(j.uuid); else onDone();
    } catch { setErr('Could not reach the server'); } finally { setBusy(false); }
  }

  return (
    <form className="card card-pad" onSubmit={submit} style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 15 }}>Record a full re-notification (in person)</h3>
      <p className="muted" style={{ fontSize: 12.5, marginTop: 0 }}>
        For one station visit where you re-stated your details. Tick what you actually told the police —
        each ticked item is cleared and linked to this single anchored notification. <b>Foreign travel is
        not listed here</b>; clear those individually. Only an in-person notification discharges a duty (s.87).
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <label className="fld" style={{ maxWidth: 180 }}><span>Date you attended</span>
          <input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="fld" style={{ flex: 1, minWidth: 160 }}><span>Reference (optional)</span>
          <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. acknowledgement no." />
        </label>
      </div>
      <div style={{ display: 'grid', gap: 4, maxHeight: 280, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
        {eligible.map((r) => (
          <label key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, padding: '3px 2px' }}>
            <input type="checkbox" checked={!!checked[r.id]} onChange={(e) => setChecked((p) => ({ ...p, [r.id]: e.target.checked }))} style={{ marginTop: 3 }} />
            <span><b>{r.rule_title}</b> <span className="badge badge-mute">{r.rule_id}</span>{r.subject_label ? <span className="muted"> · {r.subject_label}</span> : null}</span>
          </label>
        ))}
      </div>
      {err && <div style={{ color: 'var(--bad)', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ marginTop: 10 }}>
        {busy ? 'Recording…' : `Clear ${selected.length} item${selected.length === 1 ? '' : 's'} with one notification`}
      </button>
    </form>
  );
}
